import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";
const api = axios.create({ baseURL: API });

export default function QuotePreviewPage() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get(`/api/quotes/${token}/preview`)
      .then(r => {
        if (r.data.ok) {
          setQuote(r.data.quote);
          setForm({
            title: r.data.quote.title || "",
            company: r.data.quote.company || "",
            items: r.data.quote.items || [],
            total_estimate: r.data.quote.total_estimate || "",
            note: r.data.quote.note || "",
            customer_phone: r.data.quote.customer_phone || "",
          });
        }
      })
      .catch(() => setError("견적서를 찾을 수 없습니다"));
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/api/quotes/${token}/edit`, form);
      if (r.data.ok) { setSaved(true); setEditing(false); setTimeout(() => setSaved(false), 3000); }
    } catch (e) {
      alert(e.response?.data?.error || "저장 실패");
    } finally { setSaving(false); }
  };

  const updateItem = (idx, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const removeItem = (idx) => {
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const addItem = () => {
    setForm(prev => ({ ...prev, items: [...prev.items, { product_name: "", qty: 1, unit_price: 0, description: "" }] }));
  };

  if (error) return (
    <div className="dept-access-page">
      <div className="dept-access-page__inner">
        <div className="dept-access-page__mark" aria-hidden="true">견적</div>
        <div className="dept-access-page__copy">
          <span className="dept-access-page__eyebrow">일광오피스 견적서</span>
          <h1>견적서를 찾을 수 없습니다</h1>
          <p>링크가 만료되었거나 올바르지 않습니다.</p>
        </div>
      </div>
    </div>
  );

  if (!quote) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-pulse text-gray-400">로딩 중...</div>
    </div>
  );

  const canEdit = quote.status === "draft" || quote.status === "submitted";
  const statusLabels = { draft: "작성 중", submitted: "검토 대기", reviewed: "검토 완료", sent: "발송 완료" };
  const statusColors = { draft: "bg-gray-100 text-gray-600", submitted: "bg-blue-100 text-blue-700", reviewed: "bg-yellow-100 text-yellow-700", sent: "bg-green-100 text-green-700" };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {saved && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 font-medium">
          견적서가 수정되었습니다
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-[#1d1d1f] to-[#2d2d2f] px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold">{quote.title}</h1>
              <p className="text-xs text-white/50 mt-1">일광오피스 · 견적서</p>
            </div>
            <span className={`px-3 py-1 rounded-lg text-xs font-medium ${statusColors[quote.status]}`}>
              {statusLabels[quote.status]}
            </span>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Customer Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[11px] font-semibold text-gray-400 uppercase mb-1">고객명</p>
              <p className="font-medium text-gray-800">{quote.user_name}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-gray-400 uppercase mb-1">회사</p>
              <p className="font-medium text-gray-800">{quote.company || quote.user_company || "-"}</p>
            </div>
            {quote.customer_phone && (
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase mb-1">연락처</p>
                <p className="font-medium text-gray-800">{quote.customer_phone}</p>
              </div>
            )}
            <div>
              <p className="text-[11px] font-semibold text-gray-400 uppercase mb-1">작성일</p>
              <p className="font-medium text-gray-800">{quote.created_at?.slice(0, 10)}</p>
            </div>
          </div>

          {/* Rental Details */}
          {quote.rental_details && (
            <div className="bg-blue-50 rounded-xl p-4">
              <h3 className="text-xs font-bold text-blue-700 mb-2">렌탈 견적 상세</h3>
              <div className="grid grid-cols-2 gap-2 text-xs text-blue-800">
                <p>플랜: <span className="font-bold">{quote.rental_details.plan}</span></p>
                <p>월 렌탈료: <span className="font-bold">{(quote.rental_details.total || 0).toLocaleString()}원</span></p>
                <p>흑백: {(quote.rental_details.bw_pages || 0).toLocaleString()}매</p>
                <p>컬러: {(quote.rental_details.color_pages || 0).toLocaleString()}매</p>
              </div>
            </div>
          )}

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-800">견적 항목</h3>
              {canEdit && !editing && (
                <button onClick={() => setEditing(true)}
                  className="px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-medium hover:bg-blue-600 transition">
                  편집하기
                </button>
              )}
            </div>

            {editing ? (
              <div className="space-y-3">
                {form.items.map((item, i) => (
                  <div key={i} className="flex gap-2 items-start bg-gray-50 rounded-xl p-3">
                    <div className="flex-1 space-y-2">
                      <input value={item.product_name || ""} onChange={e => updateItem(i, "product_name", e.target.value)}
                        placeholder="제품명" className="w-full px-3 py-2 rounded-lg border text-sm"/>
                      <div className="flex gap-2">
                        <input type="number" value={item.qty || 1} onChange={e => updateItem(i, "qty", parseInt(e.target.value) || 1)}
                          className="w-20 px-3 py-2 rounded-lg border text-sm" placeholder="수량"/>
                        <input type="number" value={item.unit_price || 0} onChange={e => updateItem(i, "unit_price", parseInt(e.target.value) || 0)}
                          className="w-32 px-3 py-2 rounded-lg border text-sm" placeholder="단가"/>
                        <input value={item.description || ""} onChange={e => updateItem(i, "description", e.target.value)}
                          className="flex-1 px-3 py-2 rounded-lg border text-sm" placeholder="비고"/>
                      </div>
                    </div>
                    <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600 text-lg mt-1">✕</button>
                  </div>
                ))}
                <button onClick={addItem} className="w-full py-2 rounded-xl border-2 border-dashed border-gray-300 text-sm text-gray-400 hover:border-blue-400 hover:text-blue-500 transition">
                  + 항목 추가
                </button>
                <div>
                  <label className="text-xs font-semibold text-gray-400 block mb-1">메모</label>
                  <textarea value={form.note || ""} onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border text-sm h-20" placeholder="추가 요청사항"/>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-xl border text-sm text-gray-500 hover:bg-gray-50">취소</button>
                  <button onClick={handleSave} disabled={saving}
                    className="px-6 py-2 rounded-xl bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 disabled:opacity-50 transition">
                    {saving ? "저장 중..." : "저장"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-[11px] font-bold text-gray-400 uppercase">제품</th>
                      <th className="text-center px-4 py-2.5 text-[11px] font-bold text-gray-400 uppercase">수량</th>
                      <th className="text-right px-4 py-2.5 text-[11px] font-bold text-gray-400 uppercase">단가</th>
                      <th className="text-right px-4 py-2.5 text-[11px] font-bold text-gray-400 uppercase">금액</th>
                      <th className="text-center px-4 py-2.5 text-[11px] font-bold text-gray-400 uppercase">상세</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(quote.items || []).map((item, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3 font-medium text-gray-800">{item.product_name}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{item.qty}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{(item.unit_price || 0).toLocaleString()}원</td>
                        <td className="px-4 py-3 text-right font-bold text-gray-800">
                          {((item.qty || 1) * (item.unit_price || 0)).toLocaleString()}원
                        </td>
                        <td className="px-4 py-3 text-center">
                          {item.detail_slug ? (
                            <Link to={`/p/${item.detail_slug}`}
                              className="text-blue-500 hover:text-blue-700 text-xs font-medium underline">
                              상세보기 →
                            </Link>
                          ) : item.product_id ? (
                            <Link to={`/product/sktec/${item.product_id}`}
                              className="text-blue-500 hover:text-blue-700 text-xs font-medium underline">
                              상세보기 →
                            </Link>
                          ) : (
                            <span className="text-gray-300 text-xs">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={3} className="px-4 py-3 text-right text-sm font-bold text-gray-600">합계</td>
                      <td className="px-4 py-3 text-right text-base font-bold text-blue-600">
                        {quote.total_estimate ? `${parseInt(quote.total_estimate).toLocaleString()}원` : "-"}
                      </td>
                      <td/>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Notes */}
          {quote.note && (
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="text-xs font-bold text-gray-500 mb-1">메모</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{quote.note}</p>
            </div>
          )}

          {/* Admin Note */}
          {quote.admin_note && (
            <div className="bg-emerald-50 rounded-xl p-4">
              <h3 className="text-xs font-bold text-emerald-700 mb-1">담당자 메모</h3>
              <p className="text-sm text-emerald-800 whitespace-pre-wrap">{quote.admin_note}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 text-center">
          <p className="text-xs text-gray-400">일광오피스 · 복합기 렌탈 전문</p>
          <p className="text-[10px] text-gray-300 mt-1">문의: 1644-1950</p>
        </div>
      </div>
    </div>
  );
}
